import React, { useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  IconButton,
  ModalFooter,
  Icon,
  Flex,
  Text,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { IoTrashOutline } from 'react-icons/io5';
import { FiAlertCircle, FiArrowRight } from 'react-icons/fi';

import { QuestionResponse } from '../../../@types';
import { useDeleteResponse, useCurrentUser } from '../../../api';
import BlgButton from '../../../components/Button/Button';

interface ResponseFooterProps {
  myResponse: QuestionResponse | undefined;
  handleClose: () => void;
  fileAdded?: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  onSubmit: (props: Record<string, unknown>) => void;
  onDelete: () => void;
}
interface ConfirmDeleteDialogProps {
  onConfirm: () => void;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  headerText?: string;
}

export const ConfirmDeleteDialog = ({
  onConfirm,
  isOpen,
  isLoading,
  onClose,
  headerText = 'Delete Response',
}: ConfirmDeleteDialogProps) => {
  const cancelRef = useRef(null);

  return (
    <AlertDialog
      isCentered
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="medium">
            {headerText}
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure? You can&apos;t undo this action afterwards.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              isLoading={isLoading}
              colorScheme="red"
              onClick={onConfirm}
              ml={3}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export function ResponseModalFooter({
  myResponse,
  handleClose,
  fileAdded = false,
  isLoading,
  isDisabled,
  onSubmit,
  onDelete,
}: ResponseFooterProps) {
  const { data: user } = useCurrentUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    mutateAsync: deleteResponse,
    isLoading: isDeleting,
  } = useDeleteResponse(user?.id || '', myResponse?.episode_prompt_id || '');

  const onDeleteResponse = useCallback(async () => {
    if (!!myResponse) {
      await deleteResponse({ response_id: myResponse.id });
      onClose();
      handleClose();
    }
  }, [myResponse, deleteResponse]);

  return (
    <ModalFooter mb={3}>
      {!!myResponse && (
        <>
          <IconButton
            fontWeight="medium"
            variant="ghost"
            colorScheme="red"
            size="lg"
            color="red.500"
            aria-label="Delete Response"
            icon={<Icon boxSize={7} as={IoTrashOutline} />}
            onClick={onOpen}
          />
          <ConfirmDeleteDialog
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onDeleteResponse}
            isLoading={isDeleting}
          />
        </>
      )}

      <Flex
        align="center"
        justify="flex-end"
        grow={1}
        wrap={{ base: 'wrap', md: 'nowrap' }}
      >
        {!!fileAdded && (
          <Text
            as={Flex}
            alignItems="center"
            color="orange.500"
            bg="orange.50"
            fontWeight="normal"
            border="1px solid"
            borderColor="orange.200"
            fontSize="sm"
            px={4}
            py={2}
            minH={10}
            mr={5}
            rounded="md"
            w="100%"
            maxW={{ base: 'none', md: '400px' }}
            justifyContent="center"
            flexGrow={1}
            mb={{ base: 4, md: 0 }}
          >
            <Icon boxSize={5} mr={2} as={FiAlertCircle} />
            Click upload file above to finish your response!
          </Text>
        )}
        <BlgButton
          isLoading={isLoading}
          isDisabled={isDisabled}
          colorScheme="newGreen"
          leftIcon={<FiArrowRight />}
          onClick={async () => {
            await onSubmit({});
          }}
          mr={2}
          minW="110px"
          px={6}
        >
          {!myResponse ? 'Submit' : 'Save'}
        </BlgButton>
      </Flex>
    </ModalFooter>
  );
}
